import {
  CaretLeft,
  CircleNotch,
  DotsThreeVertical,
  DownloadSimple,
  User,
} from "@phosphor-icons/react";

import { LeadLabels } from "features/labels/components";

import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useActiveConversationContext } from "../../../context/ActiveConversationContext";
import useConversationExport from "../../../datahooks/useConversationExport";
import useConversationUpdates from "../../../datahooks/useConversationUpdates";
import useActiveConversationState from "../../../hooks/useActiveConversationState";
import { useNewConversation } from "../../../hooks/useNewConversation";
import ParticipantInfo from "./ParticipantInfo";
import SyncButton from "./SyncButton";

import MarkUnreadIcon from "assets/icons/mark-unread.svg?react";

interface InboxHeaderProps {
  showLeadPreview: (leadId: string) => void;
  lastSyncAt: number;
}

export default function InboxHeader({
  showLeadPreview,
  lastSyncAt,
}: InboxHeaderProps) {
  const isDesktop = useTwBreakpoint("xl");

  const { resetActiveConversation } = useActiveConversationState();
  const { activeConversation } = useActiveConversationContext();
  const { newConversation } = useNewConversation();

  const { exportConversation, isExportingConversation } =
    useConversationExport();
  const { markAsUnread } = useConversationUpdates();

  const participants = activeConversation
    ? activeConversation.participants
    : [newConversation.participant];

  const isGroup = activeConversation ? participants.length > 1 : false;

  function handleExportConversation() {
    if (activeConversation) {
      const { owner: accountId, conversation_urn: conversationUrn } =
        activeConversation;

      exportConversation({
        accountId,
        conversationUrn,
      });
    }
  }

  function onMarkUnread() {
    if (activeConversation) {
      const { owner: accountId, conversation_urn: conversationUrn } =
        activeConversation;

      markAsUnread({
        accountId,
        conversationUrn,
      });
    }
  }

  return (
    <div className="flex lg:h-[60px] h-16 w-full items-center justify-center px-3 gap-2 lg:border-b lg:border-b-black-200">
      {/* Back to conversation button */}
      <Button
        className="lg:hidden"
        variant="ghost-black"
        intent="iconOnly"
        onClick={resetActiveConversation}
      >
        <CaretLeft />
      </Button>

      {/* Conversation Info */}
      <ParticipantInfo onParticipantSelect={showLeadPreview} />

      {/* Conversation actions */}
      <div className="ml-auto flex items-center gap-3">
        {/* Conversation tags management */}
        <RenderIf condition={isDesktop && !isGroup && participants[0].is_lead}>
          <div className="max-w-[520px]">
            <LeadLabels
              labels={participants[0].labels}
              profileId={participants[0].id}
              size="sm"
              align="end"
              maxLabels={4}
            />
          </div>

          <div className="h-10 w-px bg-black-200" />
        </RenderIf>

        <div className="gap-x-2.5 flex">
          <SyncButton lastSyncAt={lastSyncAt} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                intent="iconOnly"
                variant="secondary-black"
                size="sm"
                onClick={(e) => e.stopPropagation()}
              >
                <DotsThreeVertical />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuList>
                {/* Profile preview */}
                <RestrictedComponent visibleFor={["leads_full"]}>
                  <RenderIf condition={!isGroup}>
                    <DropdownMenuItem
                      onClick={() => showLeadPreview(participants[0].id)}
                    >
                      <User />
                      View profile
                    </DropdownMenuItem>
                  </RenderIf>
                </RestrictedComponent>

                {/* Export conversation */}
                <RenderIf condition={!!activeConversation}>
                  <DropdownMenuItem onClick={handleExportConversation}>
                    {isExportingConversation ? (
                      <CircleNotch className="animate-spin" />
                    ) : (
                      <DownloadSimple />
                    )}
                    Export conversation
                  </DropdownMenuItem>
                  <RenderIf
                    condition={
                      !!activeConversation && !activeConversation.unread_count
                    }
                  >
                    <DropdownMenuItem onClick={onMarkUnread}>
                      <MarkUnreadIcon />
                      Mark as unread
                    </DropdownMenuItem>
                  </RenderIf>
                </RenderIf>
              </DropdownMenuList>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
